/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle
} from 'reactstrap';
import { formatDate, DATE } from '@utils/date';
import { Avatar } from '@g17eco/atoms/avatar';
import { escapeRegexCharacters } from '@utils/string-format';
import CompanyStatusIcon from './company-status-icon';
import {
  PERMISSION_GROUPS
} from '@utils/permission-groups';
import { generateUrl } from '../util';
import { ROUTES } from '@constants/routes';
import { InitiativeTags } from '@g17eco/types/initiative';
import { InitiativeCompany } from '@g17eco/types/initiativeSurvey';
import { setPremium, setVerifiedStatus } from '@actions/initiative';
import { isStaff } from '@selectors/user';
import { useSelector } from 'react-redux';
import { handleRouteError } from '../../logger';
import { getCompanyStatus } from '@utils/initiative';
import './styles.scss';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table, ColumnDef } from '@g17eco/molecules/table';

interface CompanyTableProps {
  portfolioId: string;
  companies: InitiativeCompany[];
  isLoadingCompanies: boolean;
  filterType: string;
  searchText: string;
  handleReload: () => void;
}

interface MenuButtonProps {
  company: InitiativeCompany;
  isUserStaff: boolean;
  disabled?: boolean;
  handleReload: () => void;
}

const MenuButton = ({ company, disabled, handleReload, isUserStaff }: MenuButtonProps) => {
  const [isOpen, setOpen] = React.useState(false);

  const handleToggleCompanyVerified = (verified: boolean) => {
    setVerifiedStatus(company._id, verified)
      .then(handleReload)
  };

  const upgradeFreeCompany = () => {
    setPremium(company._id)
      .then(() => handleReload())
      .catch(handleRouteError)
  }

  const isVerified = company.tags?.includes(InitiativeTags.VerifiedOrganization);
  const isFree = !company.permissionGroup || company.permissionGroup === PERMISSION_GROUPS.FREE;

  return (
    <Dropdown isOpen={isOpen} toggle={() => setOpen(!isOpen)}>
      <DropdownToggle disabled={disabled === true} outline={true} className='px-2'>
        <i className='fas fa-bars' />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem disabled={true} size='sm' onClick={() => alert('Coming soon')}>
          <i className='fa fa-hand-point-up mr-2' />Nudge company
        </DropdownItem>
        <DropdownItem disabled={true} size='sm' onClick={() => alert('Coming soon')}>
          <i className='far fa-comments mr-2' />Message company
        </DropdownItem>
        <DropdownItem divider />
        {isUserStaff && (
          <>
            <DropdownItem size='sm'
              onClick={() => handleToggleCompanyVerified(!isVerified)}
              className={isVerified ? 'text-ThemeDangerMedium' : 'text-ThemeSuccessMedium'}>
              <i className={`fa ${isVerified ? 'fa-times-circle' : 'fa-check-circle'} mr-2`} />
              Mark company as {isVerified ? 'not verified' : 'verified'}
              <FeatureStability stability={'internal'} />
            </DropdownItem>
            <DropdownItem size='sm'
              onClick={isFree ? upgradeFreeCompany : undefined}
              className={isFree ? '' : 'text-ThemeSuccessMedium'}>
              <i className={'fa fa-check-circle mr-2'} />
              {isFree ? 'Mark company as premium' : 'Company is premium'}
              <FeatureStability stability={'internal'} />
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  )
}

const CompanyTable = ({ portfolioId, companies: defaultCompanies, isLoadingCompanies, searchText, filterType, handleReload }: CompanyTableProps) => {

  const history = useHistory();
  const [companies, setCompanies] = React.useState(defaultCompanies);
  const isUserStaff = useSelector(isStaff);

  React.useEffect(() => {
    if (isLoadingCompanies) {
      return;
    }

    const filter = (c: InitiativeCompany) => {
      if (filterType) {
        const report = getCompanyStatus(c);
        if (report.status !== filterType) {
          return false;
        }
      }

      if (searchText) {
        const searchString = `${c.name}`.toLowerCase();
        const escapedSearchText = escapeRegexCharacters(searchText.trim().toLowerCase());
        const searchTextRegex = new RegExp(escapedSearchText.replace(/ /g, '|'), 'g');
        if ((searchString.match(searchTextRegex) || []).length === 0) {
          return false;
        }
      }
      return true;
    };
    setCompanies(defaultCompanies.filter(filter));
  }, [defaultCompanies, isLoadingCompanies, searchText, filterType]);

  const handleClick = (initiativeId: string) => {
    history.push(generateUrl(ROUTES.PORTFOLIO_TRACKER_COMPANY, { portfolioId: portfolioId, companyId: initiativeId }));
  }

  const tableColumns: ColumnDef<InitiativeCompany>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      meta: {
        cellProps: {
          className: 'nameCol',
        },
      },
      cell: ({ row }) => {
        const data = row.original;
        /** @deprecated data */
        const isPublic = 'isPublic' in data && data.isPublic;
        const tooltip = !isPublic ? 'This company is not currently public and cannot be viewed' : 'Organization';
        return (
          <div className={'d-flex align-items-center'}>
            <div className='profile mr-2'>
              <Avatar width='40px'>{data.profile ? <img alt='avatar' src={data.profile} /> : undefined}</Avatar>
            </div>
            <div className='flex-fill'>
              <SimpleTooltip text={tooltip}>
                <Button disabled={!isPublic} size='sm' color='link' onClick={() => handleClick(data._id)}>
                  {data.name}
                </Button>
              </SimpleTooltip>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'lastSurveyDate',
      header: 'Last Report',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: ({ row }) => {
        const lastSurveyDate = row.original.lastSurveyDate;
        return (
          <span className={lastSurveyDate ? '' : 'text-ThemeNeutralsLight'}>
            {lastSurveyDate ? formatDate(lastSurveyDate, DATE.MONTH_YEAR) : 'never'}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <CompanyStatusIcon company={row.original} showLabel={true} />,
    },
    {
      id: '_id',
      header: '',
      meta: {
        cellProps: {
          className: 'idCol',
        },
      },
      cell: ({ row }) => <MenuButton isUserStaff={isUserStaff} company={row.original} handleReload={handleReload} />,
    },
  ];

  const noResults = companies.length === 0;
  let noCompanysMessage = '';
  if (noResults) {
    noCompanysMessage = filterType || searchText ? 'No companies match your criteria' : 'You have no companies assigned to this reporting level';
  }

  return (
    <>
      <LoadingPlaceholder isLoading={isLoadingCompanies} height={40} />
      <LoadingPlaceholder
        isLoading={isLoadingCompanies}
        height={40}
        count={5}
        className='mt-1 background-ThemeTextWhite'
      >
        {noResults ? (
          <div className='alert alert-primary'>{noCompanysMessage}</div>
        ) : (
          <div className='company-table-container'>
            <Table columns={tableColumns} data={companies} pageSize={25} />
          </div>
        )}
      </LoadingPlaceholder>
    </>
  );
}

export default CompanyTable;
